// Système de stockage d'images local avec IndexedDB

const DB_NAME = 'LesCarnetsDeLolotte';
const STORE_NAME = 'productImages';
const DB_VERSION = 1;

// Ouvrir la base de données IndexedDB
const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'productId' });
      }
    };
  });
};

// Sauvegarder une image
export const saveProductImage = async (productId: string, file: File): Promise<string> => {
  try {
    const db = await openDB();
    
    // Convertir le fichier en base64
    const base64 = await fileToBase64(file);
    
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const imageData = {
      productId,
      base64,
      fileName: file.name,
      fileType: file.type,
      timestamp: Date.now()
    };
    
    await new Promise((resolve, reject) => {
      const request = store.put(imageData);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    return base64;
  } catch (error) {
    console.error('Erreur lors de la sauvegarde de l\'image:', error);
    throw error;
  }
};

// Récupérer une image
export const getProductImage = async (productId: string): Promise<string | null> => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const request = store.get(productId);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.base64 : null);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'image:', error);
    return null;
  }
};

// Supprimer une image
export const deleteProductImage = async (productId: string): Promise<void> => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    await new Promise((resolve, reject) => {
      const request = store.delete(productId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Erreur lors de la suppression de l\'image:', error);
    throw error;
  }
};

// Récupérer toutes les images
export const getAllProductImages = async (): Promise<Record<string, string>> => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const images: Record<string, string> = {};
        request.result.forEach((item: any) => {
          images[item.productId] = item.base64;
        });
        resolve(images);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Erreur lors de la récupération des images:', error);
    return {};
  }
};

// Convertir un fichier en base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};
